import {Box} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import ScrollContainer from 'src/common/components/scrollContainer/ScrollContainer'
import {Button} from 'src/common/generics/components/button/Button'
import PlusIcon from 'src/images/addPlusIconDarkBlueSmall.svg'
import AttachFileIcon from 'src/images/AttachFileIcon'
import TrashIcon from 'src/images/trashIcon.svg'
import PhotoCard from 'src/pages/facial-recognition/components/PhotoCard'
import MiniThumbnail from './MiniThumbnail'

type UploadPhotosProps = {
  accept?: string
  initialData?: Array<{image: string; id: string}>
  handleFiles?: (files: File[] | null) => void
  onRemoveItem?: (id: number) => void
}

export const UploadPhotos = ({initialData, onRemoveItem, accept = 'image/jpeg, image/jpg, image/png', handleFiles}: UploadPhotosProps) => {
  const [selectedFiles, setSelectedFiles] = useState<Array<{id: string; file: File}> | null>([])
  const [images, setImages] = useState<Array<{image: string; id: string}>>(initialData || [])
  const [thumbnail, setThumbnail] = useState<string | undefined>(initialData && initialData[0] ? initialData[0].image : undefined)
  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const {enqueueSnackbar} = useSnackbar()

  const handleRemoveFile = (id: string, index: number) => {
    if (hiddenFileInput.current) hiddenFileInput.current.value = ''
    if (initialData?.find((i) => i.id === id) && !isNaN(Number(id))) onRemoveItem?.(Number(id))
    setSelectedFiles((prev) => (prev ? prev?.filter((file) => file.id !== id) : null))
    setImages((prev) => {
      const newImages = prev?.filter((_, oldIndex) => oldIndex !== index)
      if (prev[index].image === thumbnail && newImages[0]) setThumbnail(newImages[0].image)
      return newImages
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      const selectedFileNames = selectedFiles?.map((file) => file.file.name)
      const newFiles = files.filter((file) => !selectedFileNames?.includes(file.name))

      if (newFiles.length > 10) return enqueueSnackbar('O limite máximo permitido é de 10 imagens', {variant: 'info'})

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i]
        const reader = new FileReader()
        const newImageId = `new_${file.name}`

        reader.readAsDataURL(file)

        reader.addEventListener('load', (event) => {
          const target = event.target as FileReader
          if (!thumbnail) setThumbnail(target.result as string)
          setImages((prev) => [...prev, {image: target.result as string, id: newImageId}])
        })
        setSelectedFiles((prev) => (prev ? [...prev, {file: file, id: newImageId}] : [{file: file, id: newImageId}]))
      }
    }
  }

  useEffect(() => {
    handleFiles?.(selectedFiles ? selectedFiles.map((file) => file.file) : null)
  }, [selectedFiles])

  useEffect(() => {
    if (selectedFiles?.length === 0 && images.length === 0) {
      setThumbnail(undefined)
    }
  }, [selectedFiles, images])

  return (
    <>
      {selectedFiles && selectedFiles.length === 0 && (!images || !images?.length) ? (
        <Box
          display='flex'
          alignSelf='center'
          boxSizing='border-box'
          border='1px dashed #D7D7D7'
          justifyContent='center'
          align='center'
          minHeight='240px'
          minWidth='480px'>
          <input type='file' multiple accept={accept} ref={hiddenFileInput} onChange={handleFileChange} style={{display: 'none'}} />
          <Button
            leftIcon={<AttachFileIcon />}
            padding='8px 16px 8px 16px'
            height='31px'
            width='137px'
            color='#009EFF'
            fontSize='12px'
            shape='5px'
            hoverColor='#1565c0'
            onClick={() => hiddenFileInput.current?.click()}>
            Fazer upload
          </Button>
        </Box>
      ) : (
        <Box display='flex' rowGap='24px' direction='column' align='center'>
          {thumbnail && (
            <PhotoCard src={thumbnail} width='auto' height='240px'>
              <Box width='100%' height='100%' display='flex' justify='flex-end'>
                <Box
                  width='fit-content'
                  padding='6px'
                  border='1px solid #F04438'
                  backgroundColor='white'
                  borderRadius='100%'
                  marginRight='-10px'
                  marginTop='-10px'
                  display='flex'
                  justify='flex-end'
                  height='fit-content'
                  style={{cursor: 'pointer'}}
                  onClick={() => {
                    const image = images.find((img) => img.image === thumbnail)
                    if (image) {
                      handleRemoveFile(
                        image.id,
                        images.findIndex((img) => img.image === thumbnail),
                      )
                    }
                  }}>
                  <img src={TrashIcon} alt='trash' width={20} height={20} style={{userSelect: 'none'}} />
                </Box>
              </Box>
            </PhotoCard>
          )}
          <Box display='flex' columnGap='16px' direction='row' align='center'>
            <ScrollContainer maxWidth='540px'>
              <Box display='flex' columnGap='12px' direction='row' align='center'>
                {images.map((item, index) => (
                  <MiniThumbnail
                    thumbnail={thumbnail}
                    src={item.image}
                    setThumbnail={setThumbnail}
                    onDelete={() => handleRemoveFile(item.id, index)}
                    key={index}
                  />
                ))}
              </Box>
            </ScrollContainer>
            <Box
              display='flex'
              alignSelf='center'
              boxSizing='border-box'
              border='1px dashed #D7D7D7'
              justifyContent='center'
              align='center'
              width='64px'
              minWidth='64px'
              height='64px'
              minHeight='64px'
              style={{cursor: 'pointer'}}
              onClick={() => hiddenFileInput.current?.click()}>
              <input type='file' multiple accept={accept} ref={hiddenFileInput} onChange={handleFileChange} style={{display: 'none'}} />

              <img src={PlusIcon} alt='ícone' />
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}
